/* eslint-disable no-restricted-syntax */
/* eslint-disable no-param-reassign */
/* eslint-disable no-use-before-define */
/* eslint-disable no-console */
/* eslint-disable react/jsx-key */
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Box,
  DrawerFooter,
  Button,
  VStack,
  Center,
  useToast,
  Portal,
  Checkbox,
  Textarea,
  FormControl,
  FormLabel,
  Divider,
  Alert,
  AlertIcon,
  AlertDescription,
  CloseButton,
  HStack,
} from '@chakra-ui/react';
import { Api } from 'api';
import { LoadingText } from 'components';
import { FormControlFormSelect } from 'core';
import { useFormik } from 'formik';
import React, { useRef, useState, useCallback } from 'react';
import { useAutoSave } from 'hooks/useAutoSave';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useMutation, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { BodyPartAnswerInfo, ExpertiseBodyDamagePartType, VehicleQueryCacheKeys } from 'types';
import { Socket } from 'socket.io-client';
import { t, Trans } from '@lingui/macro';
import { getExpertiseBackgroundColor } from '../utils/expertiseFormUtils';

export interface ExpertiseBodyworkUndercarriageControlSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  isDisabled?: boolean;
}

type ExpertiseQuestionData = {
  [key: number]: string;
};

type ExpertiseSaveData = { answers: ExpertiseQuestionData; opinionNote: string };

export const ExpertiseBodyworkUndercarriageControlSelector: React.FC<
  ExpertiseBodyworkUndercarriageControlSelectorProps
> = ({ isOpen, onClose, isDisabled }) => {
  const customer = useSelector((selector: RootState) => selector.customer.focusedCustomer);
  const allNPRef = useRef<HTMLInputElement>(null);
  const [isAllNP, setIsAllNP] = useState(false);
  const toast = useToast();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [customerSocket, setCustomerSocket] = useState<Socket | null>(null);

  const { data: questionsTemplate, isLoading: isQuestionsTemplateLoading } = useQuery(
    [VehicleQueryCacheKeys.GetBodyWorkUndercarriageControl, customer!.id],
    async () => {
      const { data: questionsResp } = await Api.Expertise.getExpertiseBodyQuestions(
        ExpertiseBodyDamagePartType.Undercarriage,
      );

      return {
        questions: questionsResp.expertiseBodyQuestions,
      };
    },
    {
      refetchOnWindowFocus: false,
      enabled: customer != null && isOpen,
      cacheTime: 0,
      onError: (err) => {
        console.error(err);
        toast({
          title: 'Alt Takım kontrol verileri çekilirken hata oluştu',
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const { data: questions, isLoading: isQuestionsLoading } = useQuery(
    VehicleQueryCacheKeys.GetBodyWorkUndercarriageControlTemplate,
    async () => {
      const { data: response } = await Api.Expertise.getCustomerBodyPartsExpertise(
        customer!.id,
        ExpertiseBodyDamagePartType.Undercarriage,
      );

      // Fetch opinion note (may not exist yet - that's OK)
      let opinionNoteData = null;
      try {
        const { data } = await Api.Expertise.getOpinionNote(customer!.id, ExpertiseBodyDamagePartType.Undercarriage);
        opinionNoteData = data;
      } catch {
        // Opinion note doesn't exist yet - use default
      }

      return {
        answers: response.expertiseBodyAnswers.reduce((accum: any, nextValue: BodyPartAnswerInfo) => {
          // eslint-disable-next-line no-param-reassign
          accum[nextValue.expertiseBodyQuestionId] = nextValue.answer;
          return accum;
        }, {} as ExpertiseQuestionData),
        opinionNote: opinionNoteData?.note || '',
      };
    },
    {
      refetchOnWindowFocus: false,
      enabled: customer != null && isOpen,
      cacheTime: 0,
      onError: (err) => {
        console.error(err);
        toast({
          title: 'Alt Takım kontrol verileri çekilirken hata oluştu',
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      answers: (questions?.answers || {}) as ExpertiseQuestionData,
      opinionNote: questions?.opinionNote || '',
    },
    onSubmit: async (data: ExpertiseSaveData) => {
      const filledAnswers = questionsTemplate?.questions.reduce((accum, next) => {
        // eslint-disable-next-line no-param-reassign
        accum[next.id] = data.answers[next.id] || '-1';
        return accum;
      }, {} as any);

      const isNotCompleted = questionsTemplate?.questions.some(
        (question) => !data.answers[question.id] || data.answers[question.id] === '-1',
      );

      if (isNotCompleted) {
        toast({
          title: 'Lütfen tüm seçimleri yapınız.',
          status: 'error',
          isClosable: true,
        });
        return;
      }
      saveExpertiseMutate.mutateAsync({ answers: filledAnswers, opinionNote: data.opinionNote });
    },
  });

  const { values, handleSubmit, setFieldValue, resetForm } = formik;
  const showLoading = !customer || !questionsTemplate || isQuestionsTemplateLoading || isQuestionsLoading || !isOpen;

  // Memoized getter for initial values - prevents auto-save from saving API data as "changes"
  const getInitialValues = useCallback(
    () => ({
      answers: (questions?.answers || {}) as ExpertiseQuestionData,
      opinionNote: questions?.opinionNote || '',
    }),
    [questions?.answers, questions?.opinionNote],
  );

  // Auto-save hook for recovery on power loss - must be before useMutation that uses clearDraft
  const { clearDraft, pendingDraft, recoverDraft, dismissDraft } = useAutoSave(
    customer?.id,
    'UndercarriageControl',
    values,
    getInitialValues,
    (recovered) => {
      if (recovered.answers) {
        Object.entries(recovered.answers).forEach(([key, value]) => {
          setFieldValue(`answers.${key}`, value);
        });
      }
      if (recovered.opinionNote !== undefined) {
        setFieldValue('opinionNote', recovered.opinionNote);
      }
    },
    isOpen && !showLoading,
  );

  const saveExpertiseMutate = useMutation<void, any, ExpertiseSaveData>(
    async (data: ExpertiseSaveData) => {
      const bodyAnswers = Object.entries(data.answers).map(([key, value]) => {
        return {
          answer: value,
          expertiseBodyQuestionId: parseInt(key, 10),
        };
      });
      await Api.Expertise.saveBodyQuestionAnswers({
        customerId: customer!.id,
        bodyDamageAnswers: bodyAnswers,
      });

      // Save opinion note
      if (data.opinionNote !== undefined) {
        await Api.Expertise.saveOpinionNote(customer!.id, ExpertiseBodyDamagePartType.Undercarriage, data.opinionNote);
      }
    },
    {
      onSuccess: () => {
        clearDraft(); // Clear auto-save draft on successful save
        onClose();
        toast({
          title: 'Başarı ile kayıt edildi',
          status: 'success',
          isClosable: true,
        });
      },
      onError: (err) => {
        console.error(err);
        toast({
          title: 'Kayıt sırasında hata oluştu',
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const selectAllNP = () => {
    if (allNPRef!.current?.checked) {
      questionsTemplate?.questions.forEach((question) => {
        const npOption = question.possibleAnswers.find((x) => x.value.toLowerCase() === 'sorunsuz');
        setFieldValue(`answers.${question.id}`, npOption?.key);
      });
      setIsAllNP(true);
    } else {
      resetForm();
      setIsAllNP(false);
    }
  };

  return (
    <Portal>
      <Drawer isOpen={isOpen} onClose={onClose} size="xl">
        <DrawerOverlay />
        <DrawerContent overflow="auto">
          <form noValidate onSubmit={handleSubmit}>
            <DrawerCloseButton disabled={showLoading || saveExpertiseMutate.isLoading} />
            <DrawerHeader>
              <Trans>Alt Takım Kontrol Formu</Trans>
            </DrawerHeader>

            <DrawerBody>
              {showLoading ? (
                <Center>
                  <LoadingText />
                </Center>
              ) : (
                <VStack gap={10} align="stretch">
                  {/* Draft Recovery Banner */}
                  {pendingDraft && (
                    <Alert status="info" borderRadius="md">
                      <AlertIcon />
                      <AlertDescription flex="1">
                        {pendingDraft.date.toLocaleString('tr-TR')} tarihli kaydedilmemiş taslak bulundu.
                      </AlertDescription>
                      <HStack spacing={2}>
                        <Button size="sm" colorScheme="blue" onClick={recoverDraft}>
                          <Trans>Geri Yükle</Trans>
                        </Button>
                        <CloseButton onClick={dismissDraft} />
                      </HStack>
                    </Alert>
                  )}
                  <Box>
                    <Checkbox ref={allNPRef} colorScheme="blue" onChange={selectAllNP} isChecked={isAllNP}>
                      <Trans>Tamamı Sorunsuz</Trans>
                    </Checkbox>
                  </Box>
                  <Box width="xs">
                    {questionsTemplate.questions.map((expertiseBodyQuestion) => {
                      const selectedValue = values.answers[expertiseBodyQuestion.id] || '-1';
                      const selectedAnswer = expertiseBodyQuestion.possibleAnswers?.find(
                        (ans) => ans.key === selectedValue,
                      );
                      const { bg, color } = getExpertiseBackgroundColor(selectedAnswer?.value);

                      return (
                        <FormControlFormSelect
                          key={`ebqid${expertiseBodyQuestion.id}`}
                          label={expertiseBodyQuestion.question}
                          placeholder={t`Seçiniz`}
                          onChange={(e) => {
                            setFieldValue(`answers.${expertiseBodyQuestion.id}`, e.target.value);
                            setIsAllNP(false);
                          }}
                          value={values.answers[expertiseBodyQuestion.id] || '-1'}
                          bg={bg}
                          sx={{
                            color,
                            '& option': {
                              color: 'black',
                            },
                            '& option:selected': {
                              color,
                            },
                          }}
                        >
                          {expertiseBodyQuestion.possibleAnswers?.map((ans) => (
                            <option value={ans.key} key={`vhsy${expertiseBodyQuestion.id}${ans.key}`}>
                              {ans.value}
                            </option>
                          ))}
                        </FormControlFormSelect>
                      );
                    })}
                  </Box>

                  <Divider my={4} />

                  {/* Kanaat Notu (Opinion Note) Section */}
                  <Box width="100%">
                    <FormControl>
                      <FormLabel fontWeight="bold">
                        <Trans>Kanaat Notu</Trans>
                      </FormLabel>
                      <Textarea
                        placeholder={t`Uzman görüşünüzü buraya yazınız...`}
                        value={values.opinionNote}
                        onChange={(e) => setFieldValue('opinionNote', e.target.value)}
                        rows={4}
                        resize="vertical"
                        maxLength={5000}
                      />
                    </FormControl>
                  </Box>
                </VStack>
              )}
            </DrawerBody>

            <DrawerFooter>
              <Button
                variant="outline"
                mr={3}
                onClick={onClose}
                disabled={showLoading || saveExpertiseMutate.isLoading}
              >
                <Trans>İptal</Trans>
              </Button>
              {!isDisabled && (
                <Button type="submit" disabled={showLoading || saveExpertiseMutate.isLoading}>
                  <Trans>Kaydet</Trans>
                </Button>
              )}
            </DrawerFooter>
          </form>
        </DrawerContent>
      </Drawer>
    </Portal>
  );
};
